import { ruleComponents } from "@p0security/shared/types/policy/constants";
import {
  AgenticRequestorRule,
  ApprovalRule,
  type AutoApprover,
  Filter,
  IntegrationResourceRule,
  Policy,
  RequestorRule,
  ResourceRule,
} from "@p0security/shared/types/policy/types";
import { validate as validateEmail } from "isemail";
import { compact, noop } from "lodash";
import { createContext, useCallback, useMemo, useState } from "react";

import { useObjectMemo } from "../../../../hooks/useObjectMemo.ts";
import { DEFAULT_NEW_REQUESTOR, DEFAULT_NEW_RESOURCE } from "../constants.ts";
import { isValidApproval } from "../utils.ts";
import { useInstalledApprovers } from "./approvers.ts";
import {
  newApprovalRuleOfType,
  newRequestorRuleOfType,
  newResourceRuleOfType,
  newResourceRuleWithFilter,
  newResourceRuleWithoutFilter,
  updateResourceAccessType,
  updateResourceService,
} from "./mutations.ts";

type EditorState = {
  name: string | undefined;
  disabled: boolean | undefined;
  resource: ResourceRule | undefined;
  requestor: RequestorRule | undefined;
  requestorError: string | undefined;
  approval: ApprovalRule[];
};

type EditorMutations = {
  // Name Mutations
  setName: (name: string) => void;

  // Enabled/disabled Mutations
  setDisabled: (disabled: boolean) => void;

  // Resource Mutations
  setResource: (resource: ResourceRule) => void;
  setResourceType: (type: ResourceRule["type"]) => void;
  setResourceService: (service: IntegrationResourceRule["service"]) => void;
  setResourceAccessType: (
    accessType: IntegrationResourceRule["accessType"]
  ) => void;
  addOrSetResourceFilter: <T extends IntegrationResourceRule>(
    filterKey: keyof T["filters"],
    filter: Filter,
    oldFilterType?: keyof T["filters"]
  ) => void;
  removeResourceFilter: (filterKey: string) => void;

  // Requestor Mutations
  setRequestor: (requestor: RequestorRule | undefined) => void;
  setRequestorType: (type: RequestorRule["type"]) => void;
  setRequestorError: (error: string | undefined) => void;

  // Approval Mutations
  setApproval: (approval: ApprovalRule[]) => void;
  setApprovalType: (index: number, type: ApprovalRule["type"]) => void;
};

type EditorComputed = {
  installedApprovers: AutoApprover[];
  isComponentValid: (component: (typeof ruleComponents)[number]) => boolean;
  isValidRule: boolean;
};

const isValidGroupRequestor = (
  requestor: Extract<RequestorRule, { type: "group" }>
) =>
  Boolean(requestor.groups.length) &&
  requestor.groups.every((g) => !!g.directory && !!g.id && !!g.label);

const isValidUserRequestor = (
  requestor: Extract<RequestorRule, { type: "user" }>
) => Boolean(requestor.uid) && validateEmail(requestor.uid);

const isValidAgentRule = (
  agent: AgenticRequestorRule["actor"] | undefined
): boolean => {
  if (!agent) return true;
  switch (agent.type) {
    case "any":
      return true;
    case "agent-client":
      return Boolean(agent.clientId);
    case "agent-owner":
      return Boolean(agent.owner) && validateEmail(agent.owner);
    case "provider":
      return Boolean(agent.providerId);
    default:
      return true;
  }
};

const isValidSubjectRule = (
  subject: AgenticRequestorRule["subject"] | undefined
): boolean => {
  if (!subject) return true;
  switch (subject.type) {
    case "any":
      return true;
    case "user":
      switch (subject.user.type) {
        case "any":
          return true;
        case "user":
          return isValidUserRequestor(subject.user);
        case "group":
          return isValidGroupRequestor(subject.user);
        default:
          return true;
      }
    case "agent":
      return isValidAgentRule(subject.agent);
    default:
      return true;
  }
};

const isValidRequestor = (requestor: RequestorRule): boolean => {
  switch (requestor.type) {
    case "any":
      return true;
    case "user":
      return isValidUserRequestor(requestor);
    case "group":
      return isValidGroupRequestor(requestor);
    case "agentic":
      return (
        isValidAgentRule(requestor.actor) &&
        isValidSubjectRule(requestor.subject)
      );
    default:
      return true;
  }
};

export const PolicyEditorContext = createContext<
  EditorState & EditorMutations & EditorComputed
>({
  name: undefined,
  setName: noop,

  disabled: undefined,
  setDisabled: noop,

  resource: undefined,
  setResource: noop,
  setResourceType: noop,
  setResourceService: noop,
  setResourceAccessType: noop,
  addOrSetResourceFilter: noop,
  removeResourceFilter: noop,

  requestor: undefined,
  requestorError: undefined,
  setRequestor: noop,
  setRequestorType: noop,
  setRequestorError: noop,

  approval: [],
  setApproval: noop,
  setApprovalType: noop,

  installedApprovers: [],

  isComponentValid: () => false,
  isValidRule: false,
});

type ProviderProps = React.PropsWithChildren<{
  initialRule?: Policy;
}>;

export const RoutingRuleEditorProvider: React.FC<ProviderProps> = ({
  children,
  initialRule,
}) => {
  const [name, setName] = useState<string>(initialRule?.name ?? "");

  const [disabled, setDisabled] = useState<boolean>(
    initialRule?.disabled ?? false
  );

  const [resource, setResource] = useState<ResourceRule | undefined>(
    initialRule?.resource ?? DEFAULT_NEW_RESOURCE
  );
  const [requestor, setRequestor] = useState<RequestorRule | undefined>(
    initialRule?.requestor ?? DEFAULT_NEW_REQUESTOR
  );
  const [requestorError, setRequestorError] = useState<string>();
  const [approval, setApproval] = useState<ApprovalRule[]>(
    initialRule?.approval ?? []
  );

  const installedApprovers = useInstalledApprovers();

  const setResourceType = useCallback((type: ResourceRule["type"]) => {
    setResource(newResourceRuleOfType(type));
  }, []);

  const setResourceService = useCallback(
    (service: IntegrationResourceRule["service"]) => {
      if (!resource) {
        return;
      }
      setResource(updateResourceService(service, resource));
    },
    [resource]
  );

  const setResourceAccessType = useCallback(
    (accessType: IntegrationResourceRule["accessType"]) => {
      if (!resource || resource.type !== "integration") {
        return;
      }
      setResource(updateResourceAccessType(accessType, resource));
    },
    [resource]
  );

  const addOrSetResourceFilter = useCallback(
    <T extends IntegrationResourceRule>(
      filterType: keyof T["filters"],
      filter: Filter,
      oldFilterType?: keyof T["filters"]
    ) => {
      if (resource?.type !== "integration") {
        return;
      }

      setResource(
        newResourceRuleWithFilter(
          resource as T,
          filterType,
          filter,
          oldFilterType
        )
      );
    },
    [resource]
  );

  const removeResourceFilter = useCallback(
    <T extends IntegrationResourceRule>(filterKey: string) => {
      if (resource?.type !== "integration") {
        return;
      }

      setResource(newResourceRuleWithoutFilter(resource as T, filterKey));
    },
    [resource]
  );

  const setRequestorType = useCallback((type: RequestorRule["type"]) => {
    setRequestor(newRequestorRuleOfType(type));
  }, []);

  const setApprovalType = useCallback(
    (index: number, type: ApprovalRule["type"]) => {
      setApproval((approval) =>
        compact(
          approval.map((a, i) =>
            i === index ? newApprovalRuleOfType(type, installedApprovers) : a
          )
        )
      );
    },
    [installedApprovers]
  );

  const isResourceValid = useMemo(() => {
    if (!resource) {
      return false;
    }
    if (resource.type === "integration") {
      return !!resource.service;
    }
    return true;
  }, [resource]);

  const isRequestorValid = useMemo(() => {
    if (!requestor) return false;
    if (requestorError) return false;

    return isValidRequestor(requestor);
  }, [requestor, requestorError]);

  const isApprovalListValid = useMemo(() => {
    return approval.length > 0 && approval.every(isValidApproval);
  }, [approval]);

  const isComponentValid = useCallback(
    (component: (typeof ruleComponents)[number]) => {
      switch (component) {
        case "resource":
          return isResourceValid;
        case "requestor":
          return isRequestorValid;
        case "approval":
          return isApprovalListValid;
        default:
          return false;
      }
    },
    [isResourceValid, isRequestorValid, isApprovalListValid]
  );

  const isValidRule = useMemo(() => {
    return isResourceValid && isRequestorValid && isApprovalListValid;
  }, [isResourceValid, isRequestorValid, isApprovalListValid]);

  const value = useObjectMemo({
    addOrSetResourceFilter,
    approval,
    disabled,
    installedApprovers,
    isComponentValid,
    isValidRule,
    name,
    removeResourceFilter,
    requestor,
    requestorError,
    resource,
    setApproval,
    setApprovalType,
    setDisabled,
    setName,
    setRequestor,
    setRequestorError,
    setRequestorType,
    setResource,
    setResourceAccessType,
    setResourceService,
    setResourceType,
  });

  return (
    <PolicyEditorContext.Provider value={value}>
      {children}
    </PolicyEditorContext.Provider>
  );
};
